import { decompressFrames, parseGIF } from "gifuct-js";
import type {
  FreezeFrameSource,
  ImageDecoderInstance,
} from "./gif-freeze-frame.interface";

function mimeFromBytes(bytes: Uint8Array, fallback: string): string {
  if (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46
  ) {
    return "image/gif";
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return "image/png";
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return "image/webp";
  }
  return fallback;
}

function looksAnimated(bytes: Uint8Array, mime: string): boolean {
  if (mime === "image/gif") return true;
  const tag =
    mime === "image/png" ? "acTL" : mime === "image/webp" ? "ANIM" : "";
  if (!tag) return false;
  const a = tag.charCodeAt(0);
  const b = tag.charCodeAt(1);
  const c = tag.charCodeAt(2);
  const d = tag.charCodeAt(3);
  for (let i = 0; i <= bytes.length - 4; i += 1) {
    if (
      bytes[i] === a &&
      bytes[i + 1] === b &&
      bytes[i + 2] === c &&
      bytes[i + 3] === d
    ) {
      return true;
    }
  }
  return false;
}

function toObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((ok, err) => {
    canvas.toBlob((blob) => {
      if (blob) ok(URL.createObjectURL(blob));
      else err(new Error("PNG encode failed"));
    }, "image/png");
  });
}

async function singleFrameUrls(input: Blob): Promise<string[]> {
  const src = URL.createObjectURL(input);
  try {
    const img = await new Promise<HTMLImageElement>((ok, err) => {
      const el = new Image();
      el.onload = () => ok(el);
      el.onerror = () => err(new Error("Image load failed"));
      el.src = src;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");
    ctx.drawImage(img, 0, 0);
    return [await toObjectUrl(canvas)];
  } finally {
    URL.revokeObjectURL(src);
  }
}

async function gifFrameUrls(buffer: ArrayBuffer): Promise<string[]> {
  const parsed = parseGIF(buffer);
  const frames = decompressFrames(parsed, true);
  if (frames.length === 0) throw new Error("GIF has no frames");

  const width = parsed.lsd.width;
  const height = parsed.lsd.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");

  const patchCanvas = document.createElement("canvas");
  const patchCtx = patchCanvas.getContext("2d");
  if (!patchCtx) throw new Error("Canvas unavailable");

  let previous: ImageData | null = null;
  const urls: string[] = [];

  for (const frame of frames) {
    if (frame.disposalType === 3) {
      previous = ctx.getImageData(0, 0, width, height);
    }

    patchCanvas.width = frame.dims.width;
    patchCanvas.height = frame.dims.height;
    const imageData = patchCtx.createImageData(
      frame.dims.width,
      frame.dims.height,
    );
    imageData.data.set(frame.patch);
    patchCtx.putImageData(imageData, 0, 0);
    ctx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);
    urls.push(await toObjectUrl(canvas));

    if (frame.disposalType === 2) {
      ctx.clearRect(
        frame.dims.left,
        frame.dims.top,
        frame.dims.width,
        frame.dims.height,
      );
    } else if (frame.disposalType === 3 && previous) {
      ctx.putImageData(previous, 0, 0);
      previous = null;
    }
  }

  return urls;
}

async function imageDecoderFrameUrls(
  buffer: ArrayBuffer,
  mime: string,
): Promise<string[]> {
  const Decoder = (
    globalThis as unknown as {
      ImageDecoder?: new (init: {
        data: BufferSource;
        type: string;
      }) => ImageDecoderInstance;
    }
  ).ImageDecoder;
  if (typeof Decoder !== "function")
    throw new Error("ImageDecoder unavailable");

  const decoder = new Decoder({ data: buffer, type: mime });
  await decoder.tracks.ready.catch(() => undefined);
  await decoder.completed.catch(() => undefined);

  let count = decoder.tracks.selectedTrack?.frameCount ?? 0;
  if (count < 1) {
    const first = await decoder.decode({ frameIndex: 0 });
    first.image.close();
    await decoder.completed.catch(() => undefined);
    count = decoder.tracks.selectedTrack?.frameCount ?? 1;
  }
  count = Math.max(1, count);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    decoder.close();
    throw new Error("Canvas unavailable");
  }

  const urls: string[] = [];
  try {
    for (let i = 0; i < count; i += 1) {
      const { image } = await decoder.decode({ frameIndex: i });
      try {
        if (canvas.width !== image.width || canvas.height !== image.height) {
          canvas.width = image.width;
          canvas.height = image.height;
        }
        ctx.globalCompositeOperation = "copy";
        ctx.drawImage(image, 0, 0);
        ctx.globalCompositeOperation = "source-over";
        urls.push(await toObjectUrl(canvas));
      } finally {
        image.close();
      }
    }
  } finally {
    try {
      decoder.close();
    } catch {
      // ignore
    }
  }
  return urls;
}

export async function openFreezeFrameSource(
  input: Blob,
): Promise<FreezeFrameSource> {
  const buffer = await input.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const mime = mimeFromBytes(bytes, input.type || "application/octet-stream");
  const animated = looksAnimated(bytes, mime);

  let frameUrls: string[];
  try {
    if (mime === "image/gif") {
      frameUrls = await gifFrameUrls(buffer);
    } else if (animated) {
      frameUrls = await imageDecoderFrameUrls(buffer, mime);
    } else {
      frameUrls = await singleFrameUrls(input);
    }
  } catch {
    frameUrls = await singleFrameUrls(input);
  }

  return {
    frameCount: frameUrls.length,
    animated: animated || frameUrls.length > 1,
    frameUrls,
    dispose: () => {
      for (const url of frameUrls) URL.revokeObjectURL(url);
      frameUrls.length = 0;
    },
  };
}

export async function openFreezeFrameSourceFromUrl(
  url: string,
): Promise<FreezeFrameSource | null> {
  try {
    const res = await fetch(url.replace(/\?t=\d+$/, ""), {
      mode: "cors",
      credentials: "omit",
    });
    if (!res.ok) return null;
    return openFreezeFrameSource(await res.blob());
  } catch {
    return null;
  }
}
